// src/design-system/components/Layout/Layout.tsx
import React from 'react';
import styles from './Layout.module.css';

interface LayoutProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
  className?: string;
}

interface ContainerProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl' | 'full';
  centered?: boolean;
  className?: string;
}

interface SectionProps extends React.HTMLAttributes<HTMLElement> {
  children: React.ReactNode;
  spacing?: 'none' | 'sm' | 'md' | 'lg' | 'xl';
  background?: 'default' | 'muted' | 'primary' | 'dark';
  className?: string;
}

interface HeaderProps extends React.HTMLAttributes<HTMLElement> {
  children: React.ReactNode;
  sticky?: boolean;
  variant?: 'default' | 'transparent' | 'bordered';
  className?: string;
}

interface MainProps extends React.HTMLAttributes<HTMLElement> {
  children: React.ReactNode;
  className?: string;
}

interface FooterProps extends React.HTMLAttributes<HTMLElement> {
  children: React.ReactNode;
  variant?: 'default' | 'minimal' | 'dark';
  className?: string;
}

interface SidebarProps extends React.HTMLAttributes<HTMLElement> {
  children: React.ReactNode;
  side?: 'left' | 'right';
  width?: 'sm' | 'md' | 'lg' | 'xl';
  className?: string;
}

interface GridProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
  cols?: 1 | 2 | 3 | 4 | 5 | 6 | 12;
  gap?: 'none' | 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  responsive?: boolean;
  className?: string;
}

interface FlexProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
  direction?: 'row' | 'col';
  align?: 'start' | 'end' | 'center' | 'baseline' | 'stretch';
  justify?: 'start' | 'end' | 'center' | 'between' | 'around' | 'evenly';
  gap?: 'none' | 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  wrap?: boolean;
  className?: string;
}

const Layout: React.FC<LayoutProps> & {
  Container: React.FC<ContainerProps>;
  Section: React.FC<SectionProps>;
  Header: React.FC<HeaderProps>;
  Main: React.FC<MainProps>;
  Footer: React.FC<FooterProps>;
  Sidebar: React.FC<SidebarProps>;
  Grid: React.FC<GridProps>;
  Flex: React.FC<FlexProps>;
} = ({ children, className = '', ...props }) => {
  const stylesRecord = styles as Record<string, string>;
  return (
    <div className={`${stylesRecord['layout'] || ''} ${className}`} {...props}>
      {children}
    </div>
  );
};

const Container: React.FC<ContainerProps> = ({
  children,
  size = 'lg',
  centered = true,
  className = '',
  ...props
}) => {
  const stylesRecord = styles as Record<string, string>;
  const containerClasses = [
    stylesRecord['container'],
    stylesRecord[`container--${size}`],
    centered && stylesRecord['container--centered'],
    className
  ].filter(Boolean).join(' ');

  return (
    <div className={containerClasses} {...props}>
      {children}
    </div>
  );
};

const Section: React.FC<SectionProps> = ({
  children,
  spacing = 'lg',
  background = 'default',
  className = '',
  ...props
}) => {
  const stylesRecord = styles as Record<string, string>;
  const sectionClasses = [
    stylesRecord['section'],
    stylesRecord[`section--${spacing}`],
    stylesRecord[`section--${background}`],
    className
  ].filter(Boolean).join(' ');

  return (
    <section className={sectionClasses} {...props}>
      {children}
    </section>
  );
};

const Header: React.FC<HeaderProps> = ({
  children,
  sticky = false,
  variant = 'default',
  className = '',
  ...props
}) => {
  const stylesRecord = styles as Record<string, string>;
  const headerClasses = [
    stylesRecord['header'],
    stylesRecord[`header--${variant}`],
    sticky && stylesRecord['header--sticky'],
    className
  ].filter(Boolean).join(' ');

  return (
    <header className={headerClasses} {...props}>
      {children}
    </header>
  );
};

const Main: React.FC<MainProps> = ({ children, className = '', ...props }) => {
  const stylesRecord = styles as Record<string, string>;
  return (
    <main className={`${stylesRecord['main'] || ''} ${className}`} {...props}>
      {children}
    </main>
  );
};

const Footer: React.FC<FooterProps> = ({
  children,
  variant = 'default',
  className = '',
  ...props
}) => {
  const stylesRecord = styles as Record<string, string>;
  const footerClasses = [
    stylesRecord['footer'],
    stylesRecord[`footer--${variant}`],
    className
  ].filter(Boolean).join(' ');

  return (
    <footer className={footerClasses} {...props}>
      {children}
    </footer>
  );
};

const Sidebar: React.FC<SidebarProps> = ({
  children,
  side = 'left',
  width = 'md',
  className = '',
  ...props
}) => {
  const stylesRecord = styles as Record<string, string>;
  const sidebarClasses = [
    stylesRecord['sidebar'],
    stylesRecord[`sidebar--${side}`],
    stylesRecord[`sidebar--${width}`],
    className
  ].filter(Boolean).join(' ');

  return (
    <aside className={sidebarClasses} {...props}>
      {children}
    </aside>
  );
};

const Grid: React.FC<GridProps> = ({
  children,
  cols = 1,
  gap = 'md',
  responsive = false,
  className = '',
  ...props
}) => {
  const stylesRecord = styles as Record<string, string>;
  const gridClasses = [
    stylesRecord['grid'],
    stylesRecord[`grid--cols-${cols}`],
    stylesRecord[`grid--gap-${gap}`],
    responsive && stylesRecord['grid--responsive'],
    className
  ].filter(Boolean).join(' ');

  return (
    <div className={gridClasses} {...props}>
      {children}
    </div>
  );
};

const Flex: React.FC<FlexProps> = ({
  children,
  direction = 'row',
  align = 'stretch',
  justify = 'start',
  gap = 'md',
  wrap = false,
  className = '',
  ...props
}) => {
  const stylesRecord = styles as Record<string, string>;
  const flexClasses = [
    stylesRecord['flex'],
    stylesRecord[`flex--${direction}`],
    stylesRecord[`flex--align-${align}`],
    stylesRecord[`flex--justify-${justify}`],
    stylesRecord[`flex--gap-${gap}`],
    wrap && stylesRecord['flex--wrap'],
    className
  ].filter(Boolean).join(' ');

  return (
    <div className={flexClasses} {...props}>
      {children}
    </div>
  );
};

// Attach sub-components
Layout.Container = Container;
Layout.Section = Section;
Layout.Header = Header;
Layout.Main = Main;
Layout.Footer = Footer;
Layout.Sidebar = Sidebar;
Layout.Grid = Grid;
Layout.Flex = Flex;

export default Layout;

